import { Directive, HostListener, inject, input } from '@angular/core';
import { Location } from '@angular/common';

/** Touch must start within this many px of the left edge to count as a back-swipe. */
const EDGE_PX = 24;
/** Movement under this many px is noise — we don't commit to an axis yet. */
const AXIS_TOLERANCE_PX = 10;
/** Horizontal travel (as a fraction of viewport width) that commits to going back. */
const COMMIT_FRACTION = 0.3;

/**
 * iOS-style edge swipe-back. A touch beginning at the left screen edge that drags rightward past a
 * third of the viewport pops history on release. Guards keep it from hijacking the page:
 *  - mouse pointers are ignored (cursor drags aren't swipes);
 *  - it only arms below the desktop breakpoint, where there's no persistent sidebar;
 *  - once the gesture reads as more vertical than horizontal it bails, leaving scrolling untouched;
 *  - `appSwipeBackDisabled` lets the host switch it off (e.g. the live match controller).
 */
@Directive({
  selector: '[appSwipeBack]',
  standalone: true,
})
export class SwipeBackDirective {
  private readonly location = inject(Location);
  readonly disabled = input(false, { alias: 'appSwipeBackDisabled' });

  private startX = 0;
  private startY = 0;
  private tracking = false;
  private committed = false;
  private willTrigger = false;

  @HostListener('pointerdown', ['$event'])
  onPointerDown(event: PointerEvent): void {
    this.tracking = false;
    if (this.disabled() || event.pointerType === 'mouse') {
      return;
    }
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      return;
    }
    if (event.clientX > EDGE_PX) {
      return;
    }
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.tracking = true;
    this.committed = false;
    this.willTrigger = false;
  }

  @HostListener('pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    if (!this.tracking) {
      return;
    }
    const dx = event.clientX - this.startX;
    const dy = event.clientY - this.startY;
    if (!this.committed) {
      if (Math.abs(dx) < AXIS_TOLERANCE_PX && Math.abs(dy) < AXIS_TOLERANCE_PX) {
        return;
      }
      // A mostly-vertical first move is a scroll, not a back-swipe — let it go.
      if (Math.abs(dy) > Math.abs(dx)) {
        this.tracking = false;
        return;
      }
      this.committed = true;
    }
    this.willTrigger = dx > window.innerWidth * COMMIT_FRACTION;
  }

  @HostListener('pointerup')
  onPointerUp(): void {
    if (this.tracking && this.willTrigger) {
      this.location.back();
    }
    this.reset();
  }

  @HostListener('pointercancel')
  onPointerCancel(): void {
    this.reset();
  }

  private reset(): void {
    this.tracking = false;
    this.committed = false;
    this.willTrigger = false;
  }
}
